/* ==========================================================================
   Young Grandpa - footer tagline scramble/decode reveal
   --------------------------------------------------------------------------
   The correct text is always in the markup (via data-text, mirrored into a
   plain, un-split .visually-hidden span) so there's no dependency on JS for
   the real content - this only transiently overwrites each letter's glyph
   during the reveal, then restores it. Non-letter characters (spaces,
   punctuation) are left alone.

   Loops while the footer is in view: each decode cycle is followed by a
   2-3s rest, then it scrambles again. The IntersectionObserver starts the
   loop on entry and pauses it on exit (clearing timers and snapping every
   character back to correct, so it's never left stuck mid-scramble
   off-screen), resuming on re-entry. A document.visibilitychange listener
   applies the same pause/resume when the tab itself is backgrounded, so a
   hidden tab doesn't keep ticking setInterval/setTimeout in the background.
   Both conditions (in view AND tab visible) are tracked separately and
   ANDed together in _sync(), so toggling one doesn't fight the other -
   scrolling the footer off-screen while the tab is hidden, then bringing
   the tab back, still requires the footer to also be in view before the
   loop restarts, and vice versa.

   Used in exactly two places, both a short all-caps-ish tagline: the footer
   tagline (blocks/yg-footer-tagline.liquid) and the About page's "Elevate,
   never settle." heading (sections/about-brand.liquid), which was asked to
   reuse this same effect rather than get its own. An earlier pass added a
   `manual` trigger mode and a `multiline` layout mode so the FAQ answer
   could reuse this mechanism too; both were removed again (see git history)
   once the FAQ redesign moved to a plain accordion reveal instead. Keep it
   to taglines - it is not a general-purpose reveal component, and the
   width-locking below assumes a single line.

   Custom element rather than a plain init() pass: Shopify replaces this
   block's DOM node wholesale on shopify:section:load in the theme editor,
   which runs disconnectedCallback then connectedCallback on the fresh node -
   that's what re-arms the observer instead of leaving a dead one. */

(function () {
  'use strict';

  var TICK_MS = 45;
  var STAGGER_MS = 35;
  var MIN_TICKS = 7;
  var MAX_TICKS = 11;
  var REST_MIN_MS = 2000;
  var REST_MAX_MS = 3000;
  var CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!?*';
  var LETTER = /[a-zA-Z]/;

  function randomGlyph() {
    return CHARSET.charAt(Math.floor(Math.random() * CHARSET.length));
  }

  function randomTicks() {
    return MIN_TICKS + Math.floor(Math.random() * (MAX_TICKS - MIN_TICKS + 1));
  }

  function randomRestMs() {
    return REST_MIN_MS + Math.random() * (REST_MAX_MS - REST_MIN_MS);
  }

  class YgScrambleText extends HTMLElement {
    connectedCallback() {
      this._timers = new Set();
      this._chars = [];
      this._active = false;
      this._intersecting = false;
      this._pageVisible = document.visibilityState !== 'hidden';

      var text = this.getAttribute('data-text') || '';
      if (!text) return;

      var display = document.createElement('span');
      display.className = 'yg-scramble-text__display';
      display.setAttribute('aria-hidden', 'true');

      for (var i = 0; i < text.length; i++) {
        var char = text[i];
        var span = document.createElement('span');
        span.textContent = char;
        display.appendChild(span);
        if (LETTER.test(char)) {
          this._chars.push({ span: span, correct: char });
        }
      }

      var label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = text;

      this.appendChild(display);
      this.appendChild(label);

      /* Different glyphs render at different widths, so as characters cycle
         through random letters during a scramble tick, the display span's
         natural (shrink-to-content) width changes slightly on every tick -
         and since it sits in normal document flow, that jitter pushes on
         whatever is next to it (the Brand column, in the footer). Locking
         the span to the CORRECT text's own rendered width means every
         subsequent tick reflows inside that fixed box instead of resizing
         it - but the lock has to happen while the element is actually laid
         out, not here in connectedCallback. Two instances of this tagline
         exist in the footer's markup (one per breakpoint - see
         assets/yg-custom.css), and whichever one the current breakpoint
         hides is `display:none` at connect time, so measuring its width now
         would read 0 and lock it there permanently - invisible forever even
         after it becomes the visible copy (e.g. a theme-editor preview pane
         that starts wide and resizes down, or resizing an already-open tab
         across the breakpoint). Locking lazily in _lockWidth(), the first
         time IntersectionObserver confirms this element is genuinely
         on-screen, guarantees a real, non-zero measurement instead. */
      display.style.display = 'inline-block';
      this._display = display;
      this._widthLocked = false;

      // footer tagline - the fixed footer always intersects, so watch its reveal spacer instead
      var spacer = this.closest('.yg-footer-section') && this.offsetParent
        ? document.querySelector('.yg-footer-reveal')
        : null;

      this._observer = new IntersectionObserver(this._onIntersect.bind(this), { threshold: 0.2 });
      this._observer.observe(spacer && spacer.offsetHeight ? spacer : this);

      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }

    disconnectedCallback() {
      this._stop();
      if (this._observer) this._observer.disconnect();
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }

    _onIntersect(entries) {
      for (var i = 0; i < entries.length; i++) {
        this._intersecting = entries[i].isIntersecting;
      }
      if (this._intersecting && !this._widthLocked) {
        this._lockWidth();
      }
      this._sync();
    }

    _lockWidth() {
      var width = this._display.getBoundingClientRect().width;
      if (width > 0) {
        this._display.style.width = width + 'px';
        this._widthLocked = true;
      }
    }

    _onVisibilityChange() {
      this._pageVisible = document.visibilityState !== 'hidden';
      this._sync();
    }

    _sync() {
      if (this._intersecting && this._pageVisible) {
        this._start();
      } else {
        this._stop();
      }
    }

    _start() {
      if (this._active) return;
      this._active = true;
      this._runCycle();
    }

    _stop() {
      this._active = false;
      this._timers.forEach(function (id) {
        window.clearTimeout(id);
        window.clearInterval(id);
      });
      this._timers.clear();
      for (var j = 0; j < this._chars.length; j++) {
        this._chars[j].span.textContent = this._chars[j].correct;
      }
    }

    _runCycle() {
      if (!this._active || !this._chars.length) return;

      var cycleMs = 0;
      for (var i = 0; i < this._chars.length; i++) {
        var delay = i * STAGGER_MS;
        this._scrambleChar(this._chars[i], delay);
        var charEndMs = delay + MAX_TICKS * TICK_MS;
        if (charEndMs > cycleMs) cycleMs = charEndMs;
      }

      var self = this;
      var nextTimerId = window.setTimeout(function () {
        self._timers.delete(nextTimerId);
        if (self._active) self._runCycle();
      }, cycleMs + randomRestMs());
      this._timers.add(nextTimerId);
    }

    _scrambleChar(entry, delay) {
      var self = this;
      var timeoutId = window.setTimeout(function () {
        self._timers.delete(timeoutId);
        var ticks = 0;
        var maxTicks = randomTicks();
        var intervalId = window.setInterval(function () {
          entry.span.textContent = randomGlyph();
          ticks++;
          if (ticks >= maxTicks) {
            window.clearInterval(intervalId);
            self._timers.delete(intervalId);
            entry.span.textContent = entry.correct;
          }
        }, TICK_MS);
        self._timers.add(intervalId);
      }, delay);
      this._timers.add(timeoutId);
    }
  }

  if (!customElements.get('yg-scramble-text')) {
    customElements.define('yg-scramble-text', YgScrambleText);
  }
})();
